/**
 * Brand Portfolio Store
 *
 * Manages UI state for the Brand Portfolio page:
 * - Saved view selection (persisted to localStorage)
 * - Visible badges selection for the Badges column (persisted to localStorage)
 *
 * Saved views are combined with the existing owner filter
 * (both apply simultaneously).
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { type TimelinePreset, getSpanRange } from '@/utils/statusUpdatePeriod';
import type { TimelineRange } from '@/components/portfolio/TimelineControls';

export type SavedView =
  | 'all'
  | 'my_clients'
  | 'at_risk'
  | 'enterprise'
  | 'onboarding'
  | 'renewal_this_month'
  | 'paused';

export type BadgeId =
  | 'lifecycle'
  | 'channel_health'
  | 'review_cliff'
  | 'carte_blanche';

export interface SavedViewConfig {
  id: SavedView;
  labelKey: string;
  defaultLabel: string;
}

export interface BadgeConfig {
  id: BadgeId;
  labelKey: string;
  defaultLabel: string;
}

export const SAVED_VIEW_CONFIGS: SavedViewConfig[] = [
  { id: 'all', labelKey: 'portfolio_view_all', defaultLabel: 'All Brands' },
  { id: 'my_clients', labelKey: 'portfolio_view_my_clients', defaultLabel: 'My Brands' },
  { id: 'at_risk', labelKey: 'portfolio_view_at_risk', defaultLabel: 'At Risk' },
  { id: 'enterprise', labelKey: 'portfolio_view_enterprise', defaultLabel: 'Enterprise Tier' },
  { id: 'onboarding', labelKey: 'portfolio_view_onboarding', defaultLabel: 'Onboarding' },
  { id: 'renewal_this_month', labelKey: 'portfolio_view_renewal', defaultLabel: 'Review This Month' },
  { id: 'paused', labelKey: 'portfolio_view_paused', defaultLabel: 'Paused' },
];

export const BADGE_CONFIGS: BadgeConfig[] = [
  { id: 'lifecycle', labelKey: 'portfolio_badge_lifecycle', defaultLabel: 'Lifecycle' },
  { id: 'channel_health', labelKey: 'portfolio_badge_channel_health', defaultLabel: 'Channel health' },
  { id: 'review_cliff', labelKey: 'portfolio_badge_review_cliff', defaultLabel: 'Review cliff' },
  { id: 'carte_blanche', labelKey: 'portfolio_badge_carte_blanche', defaultLabel: 'Carte Blanche' },
];

export const DEFAULT_VISIBLE_BADGES: BadgeId[] = ['lifecycle', 'channel_health', 'review_cliff', 'carte_blanche'];

/** Persisted shape of a per-agency Timeline range preference. `from`/`to`
 *  are only meaningful for the `'custom'` preset — relative presets
 *  (`'2w'`/`'month'`/`'quarter'`) are always re-anchored to TODAY on read via
 *  `getTimelinePref`, so a stale saved month/quarter never silently
 *  reappears when the user comes back days later. */
export interface TimelinePref {
  preset: TimelinePreset;
  from?: string;
  to?: string;
}

interface PortfolioStore {
  savedView: SavedView;
  setSavedView: (view: SavedView) => void;
  visibleBadges: BadgeId[];
  setVisibleBadges: (badges: BadgeId[]) => void;
  toggleBadge: (badge: BadgeId) => void;
  /** Map of agencyId -> saved Brand Portfolio Timeline range preference. */
  timelinePrefByAgency: Record<number, TimelinePref>;
  /**
   * Resolves the saved preference for `agencyId` into a concrete
   * `TimelineRange`. Returns `undefined` when nothing has been saved yet
   * (caller should fall back to its own default). Relative presets are
   * recomputed from `new Date()` on every call, anchored against `timeZone`
   * (the account timezone cascade — user → team → default) so the
   * re-derived boundaries match the calendar the Timeline grid renders;
   * only `'custom'` restores the exact saved `from`/`to` bounds as-is.
   */
  getTimelinePref: (agencyId: number, timeZone: string) => TimelineRange | undefined;
  /** Persists `range` as the Timeline preference for `agencyId`. */
  setTimelinePref: (agencyId: number, range: TimelineRange) => void;
}

export const usePortfolioStore = create<PortfolioStore>()(
  persist(
    (set, get) => ({
      savedView: 'all',
      setSavedView: (view: SavedView) => set({ savedView: view }),
      visibleBadges: DEFAULT_VISIBLE_BADGES,
      setVisibleBadges: (badges: BadgeId[]) => set({ visibleBadges: badges }),
      toggleBadge: (badge: BadgeId) =>
        set((state) => ({
          visibleBadges: state.visibleBadges.includes(badge)
            ? state.visibleBadges.filter((b) => b !== badge)
            : [...state.visibleBadges, badge],
        })),
      timelinePrefByAgency: {},
      getTimelinePref: (agencyId: number, timeZone: string): TimelineRange | undefined => {
        const pref = get().timelinePrefByAgency[agencyId];
        if (!pref) return undefined;
        if (pref.preset === 'custom') {
          if (!pref.from || !pref.to) return undefined;
          return { from: new Date(pref.from), to: new Date(pref.to), preset: 'custom' };
        }
        return { ...getSpanRange(pref.preset, new Date(), timeZone), preset: pref.preset };
      },
      setTimelinePref: (agencyId: number, range: TimelineRange): void => {
        set((state) => ({
          timelinePrefByAgency: {
            ...state.timelinePrefByAgency,
            [agencyId]: {
              preset: range.preset,
              from: range.from.toISOString(),
              to: range.to.toISOString(),
            },
          },
        }));
      },
    }),
    {
      name: 'portfolio-ui',
    }
  )
);
